// mu — spawnAgent + supporting helpers (resolveCliCommand,
// awaitSpawnLiveness, createOrReusePane, defaultSpawnLivenessMs,
// prestageWorkspace, finalizeAgentRow, rollbackSpawn).
//
// spawnAgent's flow is documented inline. The interesting bit is the
// `--workspace` cycle (workspace row FKs agent.name; agent row needs
// pane_id which needs workspace path as cwd) — see prestageWorkspace.
//
// Extracted from src/agents.ts as part of refactor_split_large_src_files.

import { execFile } from "node:child_process";
import { existsSync, mkdirSync, rmSync, statSync } from "node:fs";
import { basename, dirname, resolve } from "node:path";
import { setTimeout as delay } from "node:timers/promises";
import { promisify } from "node:util";
import {
  type AgentRow,
  deleteAgent,
  getAgent,
  insertAgent,
  isValidAgentName,
  MAX_AGENT_NAME_LEN,
  pendingPaneIdFor,
} from "../agents.js";
import { type CtlProbe, ctlProbe } from "../ctl/client.js";
import { CTL_SOCK_ENV, ctlSocketPath } from "../ctl/path.js";
import type { Db } from "../db.js";
import { emitEvent } from "../logs.js";
import { activeMux, type MuxBackend } from "../mux.js";
import { isJsonMode, type NextStep } from "../output.js";
import { resolveActorIdentity } from "../tasks/claim.js";
// `sleep` is the shared poll-sleep TEST SEAM (setSleepForTests), not a
// multiplexer operation, so it stays on the tmux module rather than
// joining the MuxBackend contract.
import { sleep } from "../tmux.js";
import type { VcsBackendName } from "../vcs.js";
import { createWorkspace, freeWorkspace, workspacePath } from "../workspace.js";
import { ensureWorkstreamSession, isScratchWorkstream } from "../workstream.js";
import {
  AgentDiedOnSpawnError,
  AgentExistsError,
  AgentNameExhaustedError,
  AgentSpawnCliNotFoundError,
  AgentSpawnStartupError,
} from "./errors.js";
import { withSpawnLock } from "./spawn-lock.js";

/**
 * Smallest-unused-suffix naming convention for agent names: a role
 * (lowercase alpha + digits) followed by `-<n>` where `<n>` is one or
 * more digits. Examples that pass: `worker-1`, `reviewer-2`, `scout-12`.
 * Examples that warn: `worker-tests`, `alice`, `db-leader`, `x-y-1`.
 *
 * Source: feedback ws task fb_agent_naming_convention. The dogfood report
 * was operators (LLMs included) drifting to descriptive names like
 * `worker-tests` after a few spawns, breaking the convention silently.
 * The hint surfaced by `maybeWarnNonConventionalAgentName` is a lint, not
 * a rule — `isValidAgentName` still accepts the broader
 * `^[a-z][a-z0-9_-]{0,31}$` shape.
 */
const AGENT_NAME_CONVENTION_RE = /^[a-z][a-z0-9]*(?:-[0-9]+)$/;

/**
 * Stderr lint when an agent name doesn't match the smallest-unused-suffix
 * convention. Mirrors the slugify-truncation stderr hint in cmdTaskAdd
 * (commit 28a13af): stderr-only, exit 0, suppressed under --json so
 * machine consumers stay clean. The spawn already succeeded by the time
 * this runs — we're just nudging the operator toward `<role>-<n>`.
 */
function maybeWarnNonConventionalAgentName(name: string): void {
  if (AGENT_NAME_CONVENTION_RE.test(name)) return;
  if (isJsonMode()) return;
  process.stderr.write(
    `hint: agent name "${name}" does not match the smallest-unused-suffix convention (<role>-<n>; e.g. worker-1, reviewer-2). Accepted; consider renaming if you spawn additional workers.\n`,
  );
}

/**
 * Resolve the actual executable to launch in an agent's pane for a given
 * `cli`. Honours the env var `MU_<UPPER_CLI>_COMMAND` (e.g. `MU_PI_COMMAND=
 * pi-alt` makes `--cli pi` actually exec `pi-alt`). Falls back to the cli
 * name itself, which is what users expect when their `pi` binary is on
 * `$PATH` under that exact name.
 *
 * Used by `spawnAgent` to pick the spawned command, and by reconcile's
 * orphan detector so externally-spawned panes running the resolved binary
 * are still recognised as agents.
 */
export function resolveCliCommand(cli: string): string {
  const envName = envVarNameForCli(cli);
  const override = process.env[envName];
  return override && override.trim() !== "" ? override : cli;
}

/**
 * Compute the `MU_<UPPER_CLI>_COMMAND` env var name mu consults when
 * resolving `--cli <key>`. Hyphens in the cli key become underscores
 * (env var names can't contain `-`); this matches the operator-aliases
 * convention documented in the mu skill (e.g. `--cli pi-meta` →
 * `MU_PI_META_COMMAND`).
 */
export function envVarNameForCli(cli: string): string {
  return `MU_${cli.toUpperCase().replace(/-/g, "_")}_COMMAND`;
}

/**
 * Resolve `--cli <key>` to its actual command string AND tell the
 * caller whether the resolution came from a `MU_<UPPER_CLI>_COMMAND`
 * env var or fell through to the bare cli name. The CLI uses this to
 * surface env-var attribution in the spawn-success line so config
 * issues are visible without `mu agent show`
 * (fb_agent_spawn_no_validation, part C).
 */
export function resolveCliCommandWithSource(cli: string): {
  command: string;
  envVar: string;
  resolvedFromEnv: boolean;
} {
  const envVar = envVarNameForCli(cli);
  const override = process.env[envVar];
  if (override !== undefined && override.trim() !== "") {
    return { command: override, envVar, resolvedFromEnv: true };
  }
  return { command: cli, envVar, resolvedFromEnv: false };
}

// ─── Pre-flight PATH check (fb_agent_spawn_no_validation, part A) ──
//
// Operator typo'd `mu agent spawn worker-1 --cli pi-meta` on a host
// where `pi-meta` wasn't on PATH; the spawn appeared to succeed but
// the pane died with `command not found` and the existing 1.5s
// liveness check sometimes missed it (shells stay alive past a failed
// exec). Pre-flight the PATH resolution so a typo never:
//   1. creates an orphan workspace dir
//   2. creates an orphan tmux pane
//   3. inserts a half-spawned DB row
// The check fires AFTER `resolveCliCommand` so `MU_<UPPER>_COMMAND`
// overrides are honoured AND the diagnostic mentions the env var by
// name. Hookable via `setCommandResolverForTests` so the test suite
// can simulate "binary present" / "binary absent" without polluting
// the real PATH.

export interface CommandResolutionResult {
  ok: boolean;
  /** First whitespace-separated token of the command — the binary
   *  whose presence on PATH we checked. */
  binary: string;
  /** Absolute path of the resolved binary on PATH, when ok=true. */
  resolvedPath?: string;
}

export type CommandResolver = (command: string) => Promise<CommandResolutionResult>;

const execFileP = promisify(execFile);

/** Default resolver: shell-out to `command -v <binary>` (POSIX-portable
 *  equivalent of `which`). Returns ok=false when the lookup exits
 *  non-zero. Always returns the parsed `binary` field so the typed
 *  error can quote what we actually searched for. */
async function defaultCommandResolver(command: string): Promise<CommandResolutionResult> {
  const binary = parseFirstToken(command);
  if (binary === "") return { ok: false, binary };
  try {
    // `command -v` is the POSIX builtin lookup. Spawned via /bin/sh
    // -c so the builtin is available (it's not an external program).
    const { stdout } = await execFileP("/bin/sh", ["-c", `command -v -- ${shellQuote(binary)}`], {
      env: process.env,
    });
    const resolvedPath = stdout.trim();
    if (resolvedPath === "") return { ok: false, binary };
    return { ok: true, binary, resolvedPath };
  } catch {
    return { ok: false, binary };
  }
}

/** Single-quote a token for /bin/sh -c. Only used on the binary name,
 *  which `parseFirstToken` already restricted to a non-whitespace run
 *  — no embedded single-quotes expected. Escape defensively anyway. */
function shellQuote(s: string): string {
  return `'${s.replace(/'/g, "'\\''")}'`;
}

/** First whitespace-separated token of a command string ("pi-meta
 *  --no-solo" → "pi-meta"). Returns "" on an empty / whitespace-only
 *  input. */
function parseFirstToken(command: string): string {
  const trimmed = command.trim();
  if (trimmed === "") return "";
  const match = trimmed.match(/^\S+/);
  return match ? match[0] : "";
}

let activeCommandResolver: CommandResolver = defaultCommandResolver;

/** Override the PATH resolver. Tests use this to simulate "binary
 *  absent" / "binary present" without depending on what's actually
 *  installed. Production callers should never touch this. */
export function setCommandResolverForTests(resolver: CommandResolver): void {
  activeCommandResolver = resolver;
}

/** Restore the default PATH resolver. */
export function resetCommandResolverForTests(): void {
  activeCommandResolver = defaultCommandResolver;
}

/**
 * Verify the first token of `command` resolves to a binary on PATH.
 * Public so tests can call it directly; spawnAgent calls it before
 * prestageWorkspace so a bad --cli never creates an orphan workspace.
 */
export async function checkCommandResolvable(command: string): Promise<CommandResolutionResult> {
  return activeCommandResolver(command);
}

export interface SpawnAgentOptions {
  name: string;
  workstream: string;
  /** Defaults to "pi". Any CLI is accepted: pi is driven over its control
   *  socket; others run `MU_<CLI>_COMMAND` (or the cli name) and get
   *  text by mux paste. */
  cli?: string;
  /** The actual command to run in the pane. Defaults to the cli value. */
  command?: string;
  /** Window name to group this agent under. Defaults to the agent's name
   *  (so each agent gets its own window). Multiple agents sharing a `tab`
   *  share a window with multiple panes. */
  tab?: string;
  /** "full-access" (default) or "read-only". The schema stores it; today
   *  the role isn't enforced (deferred to a future capabilities pass). */
  role?: string;
  /** Initial working directory for the spawned pane (`tmux -c <path>`).
   *  When `workspace: true` is passed, this is ignored — the workspace
   *  path is used instead. */
  cwd?: string;
  /** Override the tmux session name. Defaults to `mu-<workstream>`. */
  tmuxSession?: string;
  /** Auto-create a VCS workspace for this agent before spawning the
   *  pane and use the workspace path as cwd. Backend defaults to
   *  detection (jj > sl > git > none). */
  workspace?: boolean;
  /** Force a specific VCS backend (only meaningful with `workspace: true`). */
  workspaceBackend?: VcsBackendName;
  /** Optional ref to base the workspace on (only meaningful with
   *  `workspace: true`). Backend-specific. */
  workspaceFrom?: string;
  /** Project root the workspace branches from (only meaningful with
   *  `workspace: true`). Defaults to `process.cwd()`. */
  workspaceProjectRoot?: string;
  /** Run the control-socket handshake for pi agents (default true).
   *  `false` skips it and reports `ctl: "skipped"`. */
  ctl?: boolean;
  /** When `name` is taken in the workstream, take the next free one
   *  (see `nextFreeAgentName`) instead of failing with AgentExistsError. */
  nextFree?: boolean;
}

/** How many taken names a `nextFree` spawn skips past before it gives up. */
const NEXT_FREE_ATTEMPTS = 32;

/** How many names `nextFreeAgentName` looks up before it gives up. */
const NEXT_FREE_LOOKUPS = 1000;

/**
 * `name` when it is free in `workstream`, else the next free name: a
 * trailing `-<n>` counts up (`delegate-1` → `delegate-2`), and a name
 * without one gets `-2`, `-3`, … (`delegate-review` → `delegate-review-2`).
 * The stem is cut so a candidate stays within the 32-char name limit.
 * A name whose workspace dir is still on disk counts as taken: a dead
 * agent's checkout outlives its row (kept as evidence), and spawning
 * that name with --workspace would fail on the dir instead of moving on.
 * Throws AgentNameExhaustedError after NEXT_FREE_LOOKUPS taken names,
 * or when no valid candidate is left (the suffix alone outgrows it).
 */
export function nextFreeAgentName(db: Db, name: string, workstream: string): string {
  const free = (n: string) =>
    getAgent(db, n, workstream) === undefined && !existsSync(workspacePath(workstream, n));
  if (free(name)) return name;
  const m = /^(.*)-(\d+)$/.exec(name);
  const [stem, first] = m ? [m[1] ?? "", BigInt(m[2] ?? "1")] : [name, 1n];
  for (let i = 1n; i <= BigInt(NEXT_FREE_LOOKUPS); i++) {
    const suffix = `-${first + i}`;
    const candidate = `${stem.slice(0, MAX_AGENT_NAME_LEN - suffix.length)}${suffix}`;
    if (!isValidAgentName(candidate)) {
      throw new AgentNameExhaustedError(name, `no valid name left (tried ${candidate})`);
    }
    if (free(candidate)) return candidate;
  }
  throw new AgentNameExhaustedError(name, `the next ${NEXT_FREE_LOOKUPS} names are taken`);
}

/** Outcome of the spawn-time control-socket handshake. */
export type SpawnCtl = "ok" | "missing" | "refused" | "skipped";

/** A ctl probe's outcome as spawn and adopt report it. */
export function probeToSpawnCtl(kind: CtlProbe["kind"]): Exclude<SpawnCtl, "skipped"> {
  return kind === "ok" ? "ok" : kind === "missing" ? "missing" : "refused";
}

/** What spawnAgent returns: the registered row plus the handshake outcome. */
export type SpawnedAgent = AgentRow & {
  ctl: SpawnCtl;
  ctlSocket: string;
  /** Set when the startup scan saw a Claude trust dialog parked in the pane. */
  trustDialog?: true;
};

/**
 * True when the agent runs pi, so the mu pi extension should answer on
 * its control socket: `cli` is "pi", or the command's argv0 basename is
 * `pi` / `pi-meta`.
 */
export function speaksMuCtl(cli: string, command: string): boolean {
  if (cli === "pi") return true;
  const argv0 = basename(parseFirstToken(command));
  return argv0 === "pi" || argv0 === "pi-meta";
}

/**
 * Handshake budget in ms after the agent is live. 0 disables. Override
 * via `MU_SPAWN_CTL_MS`.
 */
export function defaultSpawnCtlMs(): number {
  const raw = process.env.MU_SPAWN_CTL_MS;
  if (raw === undefined) return 30_000;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed) || parsed < 0) return 30_000;
  return parsed;
}

/** Cadence of `onMiss` (pane-alive check + capture: ~2 forks per call). */
const CTL_POLL_INTERVAL_MS = 250;
/** Cadence of the socket-path stat, which costs no fork. */
const CTL_STAT_INTERVAL_MS = 50;

/**
 * Poll the control socket until the extension answers or the budget
 * runs out. Returns the last probe outcome. The socket path is stat'd
 * every ~50 ms and probed as soon as it exists, so readiness is seen
 * within ~50 ms of pi binding it. `onMiss` (the pane-alive check) keeps
 * its 250 ms cadence plus one final check at the deadline, and may
 * throw to abort (the pane died); otherwise this never throws. Uses real
 * timers, not the `sleep` seam: tests stub that to a no-op, which would
 * turn this into a busy loop.
 */
async function awaitCtlHandshake(
  sock: string,
  onMiss?: () => Promise<void>,
): Promise<Exclude<SpawnCtl, "skipped">> {
  const budgetMs = defaultSpawnCtlMs();
  const deadline = Date.now() + budgetMs;
  let last: Exclude<SpawnCtl, "skipped"> = "missing";
  let lastMissAt = Number.NEGATIVE_INFINITY;
  let first = true;
  for (;;) {
    if (first || existsSync(sock)) {
      first = false;
      const remaining = deadline - Date.now();
      const probe = await ctlProbe(
        sock,
        Math.max(1, Math.min(remaining, CTL_POLL_INTERVAL_MS * 4)),
      );
      if (probe.kind === "ok") return "ok";
      last = probeToSpawnCtl(probe.kind);
    }
    // The final check at the deadline always runs, throttle or not: a pane
    // that dies in the last <250 ms keeps the scrollback onMiss captured.
    const atDeadline = Date.now() >= deadline;
    if (onMiss && (atDeadline || Date.now() - lastMissAt >= CTL_POLL_INTERVAL_MS)) {
      lastMissAt = Date.now();
      await onMiss();
    }
    const left = deadline - Date.now();
    if (left <= 0) return last;
    await delay(Math.min(CTL_STAT_INTERVAL_MS, left));
  }
}

/**
 * Spawn a new agent in its tmux pane and register it in the DB.
 *
 * Phases:
 *   1. Validate name + uniqueness.
 *   2. If --workspace: prestageWorkspace() (placeholder agent row +
 *      workspace dir + workspace row).
 *   3. createOrReusePane() in the workspace path (or opts.cwd).
 *   4. setPaneTitle + enableMuPaneBordersForPane.
 *   5. finalizeAgentRow() — patch placeholder pane_id to real (workspace
 *      path), or insert a fresh agent row (no-workspace path).
 *   6. Liveness: awaitSpawnLiveness() (fixed sleep, then pane + scan
 *      check), or for a tmux pi agent the ctl handshake with a pane
 *      check per tick and the scan at the end. herdr: startAgentInPane.
 *   7. ctl handshake for herdr pi agents (outside the rollback).
 *
 * Failure between any of (3)–(6) calls rollbackSpawn() to undo the
 * pane + row + workspace. The caller-visible error is preserved.
 */
export async function spawnAgent(db: Db, opts: SpawnAgentOptions): Promise<SpawnedAgent> {
  if (opts.nextFree === true) {
    // A parallel spawn can take the picked name between the pick and the
    // insert (the in-lock re-check throws before any side effect): pick again.
    for (let attempt = 1; ; attempt++) {
      const name = nextFreeAgentName(db, opts.name, opts.workstream);
      try {
        return await spawnAgent(db, { ...opts, name, nextFree: false });
      } catch (err) {
        if (!(err instanceof AgentExistsError) || attempt >= NEXT_FREE_ATTEMPTS) throw err;
      }
    }
  }
  if (!isValidAgentName(opts.name)) {
    throw new TypeError(
      `invalid agent name: ${JSON.stringify(opts.name)} (expected /^[a-z][a-z0-9_-]{0,31}$/)`,
    );
  }
  // Per-workstream uniqueness check: v5 allows the same agent name in
  // different workstreams. Scope the existence check to the spawn's
  // workstream so two operators spawning 'worker-1' in wsA and wsB
  // both succeed (bug_v5_name_clash_silent_misroute). A fast path only:
  // a parallel spawn of the same name can pass it too, so the check is
  // repeated inside the spawn lock, and insertAgent maps the UNIQUE
  // violation to AgentExistsError as the last line.
  if (getAgent(db, opts.name, opts.workstream) !== undefined) {
    throw new AgentExistsError(opts.name);
  }

  const session = opts.tmuxSession ?? `mu-${opts.workstream}`;
  const windowName = opts.tab ?? opts.name;
  const cli = opts.cli ?? "pi";
  const resolved = resolveCliCommandWithSource(cli);
  const command = opts.command ?? resolved.command;
  // Where `command` came from. Backends that resolve the executable
  // themselves may not silently ignore an operator's choice, and need to
  // know WHICH knob to name (see `StartAgentInPaneOptions.commandSource`).
  const commandSource =
    opts.command !== undefined ? "explicit" : resolved.resolvedFromEnv ? "env" : "cli-key";

  // Pre-flight PATH resolution. Catches `--cli does-not-exist` (and
  // typos in `MU_<UPPER>_COMMAND` overrides) before any side effect.
  // See `checkCommandResolvable` / `AgentSpawnCliNotFoundError` for
  // the rationale; in short: prevents orphan workspace dirs + orphan
  // panes + half-spawned DB rows on a typo.
  //
  // We do NOT pre-flight an explicit `--command "..."` value: the
  // operator who passed --command gave us the literal string they
  // want spawned (often a wrapper like `pi-meta --no-solo` whose
  // first token IS on PATH but with arg-quoting that the resolver
  // would mis-parse; or a shell snippet like `bash -lc '...'`). For
  // those, the existing post-spawn liveness check is the safety net.
  if (opts.command === undefined) {
    const check = await checkCommandResolvable(command);
    if (!check.ok) {
      throw new AgentSpawnCliNotFoundError(cli, check.binary, envVarNameForCli(cli));
    }
  }

  // Workspace pre-stage. See `prestageWorkspace` for the FK-ordering
  // rationale. Returns the workspace path (used as the pane's cwd) or
  // undefined when --workspace wasn't requested.
  let workspacePathStr: string | undefined;
  if (opts.workspace) {
    try {
      workspacePathStr = await prestageWorkspace(db, opts, cli);
    } catch (err) {
      emitSpawnFailed(db, opts, "workspace", err);
      throw err;
    }
  }

  // Inject identity env vars into the pane so anything running inside
  // (pi extensions, claim-protocol scripts, status segments) can branch
  // on 'I am a mu-managed worker' without scraping pane titles or DB
  // lookups. Set via tmux `-e KEY=VALUE` (per-pane; doesn't pollute the
  // tmux server's global env).
  //
  // These are NOT exposed via SpawnAgentOptions — mu identity is not
  // user-tunable. Adding more keys here means every spawned pane sees
  // them automatically.
  //
  // MU_CTL_SOCK goes to every CLI (harmless for non-pi). Its directory
  // must exist before the pane starts: for a remote agent, ssh's
  // `-L <local>:<remote>` binds the LOCAL path and does not mkdir. A
  // socket file left by a previous agent of this name is removed inside
  // the spawn lock below, once this spawn owns the name, since ssh
  // refuses to bind over it.
  //
  // The base is the DB's directory: in production that is exactly
  // ctlSocketPath's own default (MU_DB_PATH dir or the state dir), and
  // a test DB in a temp dir keeps its sockets there too.
  const ctlSocket = ctlSocketPath(opts.workstream, opts.name, dirname(db.name));
  mkdirSync(dirname(ctlSocket), { recursive: true, mode: 0o700 });
  const paneEnv: Record<string, string> = {
    MU_MANAGED_AGENT: "1",
    MU_AGENT_NAME: opts.name,
    MU_WORKSTREAM: opts.workstream,
    [CTL_SOCK_ENV]: ctlSocket,
  };
  // tmux panes inherit the tmux server's env, not ours: pin the DB (and
  // state dir) this spawn used, resolved against our cwd, so the worker's
  // `mu` hits the same taskgraph. Local spawns only.
  if (db.name !== "" && db.name !== ":memory:") paneEnv.MU_DB_PATH = resolve(db.name);
  if (process.env.MU_STATE_DIR) paneEnv.MU_STATE_DIR = resolve(process.env.MU_STATE_DIR);
  // mu sessions are created detached and may never be attached, so tmux's
  // update-environment never refreshes them and a pane would inherit a
  // stale SSH_AUTH_SOCK. Forward the caller's. File-type check only (is it
  // a socket?), not a liveness probe: mu stays ssh-free. Best-effort.
  const sshSock = process.env.SSH_AUTH_SOCK;
  if (sshSock) {
    try {
      if (statSync(sshSock).isSocket()) paneEnv.SSH_AUTH_SOCK = sshSock;
    } catch {
      // missing or unreadable: skip forwarding
    }
  }

  const hasWorkspace = workspacePathStr !== undefined;

  // Single outer try wrapping every step that can throw AFTER the
  // workspace has been prestaged: pane create/reuse, pane title +
  // borders, agent-row finalize, liveness check. Earlier shape had
  // createOrReusePane outside the try, so a tmux failure (e.g. no
  // workstream session and tmux refusing to create one) left the
  // prestaged workspace dir + placeholder agent row behind as an
  // orphan — task agent_spawn_abort_leaves_orphan_workspace.
  //
  // paneId is undefined until createOrReusePane returns; rollbackSpawn
  // skips killPane in that case. The inner try/catches that previously
  // wrapped finalize and liveness were folded into this single catch
  // (clarity > belt-and-suspenders; rollbackSpawn is idempotent and
  // best-effort regardless).
  let paneId: string | undefined;
  let agent: AgentRow;
  // Whether the agent row is this spawn's to delete on rollback. A
  // spawn that lost a same-name race must not delete the winner's row.
  let ownsRow = hasWorkspace;
  const wantsCtl = opts.ctl !== false && speaksMuCtl(cli, command) && defaultSpawnCtlMs() !== 0;
  let ctl: SpawnCtl | undefined;
  // Last phase entered, recorded in agent.spawn_failed if we roll back.
  let phase: SpawnPhase = "pane";
  let trustDialog = false;
  try {
    // Cross-process critical section: the tmux topology check-then-act
    // (`sessionExists` → `new-session` / `new-window` / `split-window`)
    // plus the agent-row finalize. Serialised per tmux SESSION so a
    // parallel fan-out (`for n in …; do mu agent spawn … & done`) can't
    // race six `new-session -d -s mu-scratch` calls into
    // five-rolled-back-losers. Keyed on the session name: spawns into
    // different sessions never contend. See spawn-lock.ts.
    //
    // The liveness wait is deliberately OUTSIDE the lock: it is the slow
    // part (1.5s+), and holding the lock across it would serialise the
    // very parallelism the operator asked for. Once the row is inserted
    // and the lock released, the next spawn can build its own window
    // while this one waits.
    const created = await withSpawnLock(session, async () => {
      // Re-check under the lock: a parallel spawn of the same name may
      // have inserted its row since the fast-path check. (With a
      // workspace, the placeholder row already claimed the name.)
      if (!hasWorkspace && getAgent(db, opts.name, opts.workstream) !== undefined) {
        throw new AgentExistsError(opts.name);
      }
      // The name is ours: no live agent owns a socket at this path.
      rmSync(ctlSocket, { force: true });
      const pid = await createOrReusePane({
        session,
        keepSessionAlive: isScratchWorkstream(opts.workstream),
        windowName,
        command,
        cwd: workspacePathStr ?? opts.cwd,
        env: paneEnv,
      });
      // Capture the pane id on the OUTER scope as soon as it exists so a
      // throw later in the lock (setPaneTitle / finalizeAgentRow INSERT
      // failure) still routes through rollbackSpawn -> killPane. Assigning
      // only via the returned object meant a finalize failure left paneId
      // undefined and rollbackSpawn skipped killPane, leaking the pane
      // (finding_verbs_spawn_rolls_back_pane).
      paneId = pid;
      const mux = await activeMux();
      // Lock first so an early OSC title cannot be frozen in; best-effort
      // (identity prefers $MU_AGENT_NAME, reconcile repairs titles).
      await mux.lockPaneTitle(pid).catch(() => {});
      await mux.setPaneTitle(pid, opts.name);
      // Apply the mu pane border to the new window. Window-scoped option;
      // see enableMuPaneBorders docstring for why this is required per
      // window (and not just per session). Self-checks MU_BANNER_QUIET
      // and is best-effort — the border is decorative.
      await mux.enableMuPaneBordersForPane(pid);
      phase = "finalize";
      const row = finalizeAgentRow(db, { opts, cli, paneId: pid, hasWorkspace });
      ownsRow = true;
      return { pid, row };
    });
    paneId = created.pid;
    agent = created.row;
    phase = "liveness";
    // Liveness check: wait briefly, then verify the pane is still alive.
    // Catches the silent-spawn-failure class of bugs where the CLI dies
    // immediately (lock conflict, bad credentials, etc.). On failure,
    // surface a typed error with whatever scrollback tmux still has.
    //
    // On a backend with no create-and-run form the pane above is a BARE
    // SHELL and this is where the agent actually starts. Both branches
    // live outside the spawn lock on purpose — they are the slow part
    // (1.5s+ here, up to herdr's 30s there) and holding the lock across
    // either would serialise the very fan-out the operator asked for.
    // Either branch throwing routes through the same `rollbackSpawn`,
    // so a pane that never became an agent never keeps its agent row.
    //
    // A tmux pi agent skips the fixed sleep: the ctl handshake is the
    // readiness signal, so the pane check rides on each handshake tick
    // and the startup-error scan runs once the handshake ends. A
    // handshake timeout with a live, clean pane is still NOT a
    // rollback (see below). MU_SPAWN_LIVENESS_MS=0 disables the checks
    // here too.
    const startAgent = (await activeMux()).startAgentInPane;
    if (startAgent === undefined) {
      if (wantsCtl) {
        const pid = paneId;
        const check = defaultSpawnLivenessMs() !== 0;
        const when = `during the ctl handshake (up to ${defaultSpawnCtlMs()}ms after spawn)`;
        // Capture every tick and keep the last good buffer: on real tmux
        // capture-pane fails once the pane is gone, so a capture taken
        // after the death check would always come back empty.
        let lastScrollback: string | undefined;
        const assertPaneAlive = async (): Promise<void> => {
          const mux = await activeMux();
          const sb = await mux.capturePane(pid, { lines: 50 }).catch(() => undefined);
          lastScrollback = sb ?? lastScrollback;
          if (await mux.paneExists(pid)) return;
          throw new AgentDiedOnSpawnError(opts.name, pid, lastScrollback, command, when);
        };
        ctl = await awaitCtlHandshake(ctlSocket, check ? assertPaneAlive : undefined);
        // Known gap: pi binds the socket in session_start (init), before
        // it renders startup diagnostics, so an ok on the first tick can
        // scan a pane that has not printed its error yet.
        //
        // An ok handshake proves the CLI started, so only provider/auth
        // patterns apply: a shell exec-failure line in the tail is then
        // transcript text (a resumed `--session` re-renders old tool
        // output such as `grep: x: No such file or directory`).
        if (check)
          trustDialog = await checkSpawnHealth(pid, opts.name, command, when, ctl === "ok");
      } else {
        trustDialog = await awaitSpawnLiveness(paneId, opts.name, command);
      }
    } else {
      // `startAgentInPane` returns only once the MUX has detected the
      // agent in that pane and considers it ready for input — strictly
      // stronger than what awaitSpawnLiveness's scrollback poll can
      // prove, so MU_SPAWN_LIVENESS_MS is
      // subsumed and deliberately not consulted. Branching on the
      // CAPABILITY, never on `mux.name`.
      await startAgent({ paneId, name: opts.name, cli, command, commandSource });
    }
  } catch (err) {
    // Record first: a throwing cleanup must neither skip the record nor
    // mask the original error.
    emitSpawnFailed(db, opts, phase, err);
    try {
      await rollbackSpawn(db, opts.name, paneId, hasWorkspace, ownsRow, opts.workstream);
    } catch (cleanupErr) {
      if (err instanceof Error && err.cause === undefined) {
        try {
          err.cause = cleanupErr;
        } catch {
          // frozen error: keep original as-is
        }
      }
    }
    if (hasWorkspace) attachOrphanCleanupHint(err, opts.name, opts.workstream);
    throw err;
  }
  // Control-socket handshake when the tmux path above did not run it.
  // Outside the rollback: a timeout means only control is missing; the
  // pane and pi are fine. The caller reports it (see
  // AgentCtlUnreachableError).
  ctl ??= wantsCtl ? await awaitCtlHandshake(ctlSocket) : "skipped";
  // `agents` is machine-local (it holds pane_id), so no capture trigger
  // covers it and this emit is the ONLY record. Typed intent so it
  // renders through the same formatter as captured ops.
  emitEvent(
    db,
    opts.workstream,
    "agent.spawn",
    `agent spawn ${opts.name} (cli=${cli}, role=${opts.role ?? "full-access"}, pane=${paneId}, ctl=${ctl})`,
    await resolveActorIdentity(),
  );
  // Lint-grade stderr nudge when the operator picked a name that
  // doesn't fit the <role>-<n> smallest-unused-suffix convention. The
  // spawn already succeeded; this is advice, not an error. Done LAST
  // so the hint trails the spawn's own stdout output and so a partial
  // failure (rolled back above) never emits it.
  maybeWarnNonConventionalAgentName(opts.name);
  return { ...agent, ctl, ctlSocket, ...(trustDialog ? { trustDialog: true as const } : {}) };
}

/**
 * Stage 1 of `--workspace` spawn: insert the agent row with a placeholder
 * pane id, then create the VCS workspace (whose row FKs the agent name).
 *
 * Why placeholder-first? `vcs_workspaces.agent` FK + ON DELETE CASCADE
 * means the workspace row needs an agents row to exist before we insert
 * it; but we can't insert agents without a pane_id (NOT NULL), and we
 * can't create the pane until we know the workspace's on-disk path
 * (used as cwd). The placeholder unblocks the cycle.
 *
 * The placeholder is a publicly-visible quirk — see `PENDING_PANE_PREFIX`
 * and the consumers it documents (refreshAgentTitle and reconcile's
 * pending-pane prune skip). The deeper fix (eliminate the placeholder
 * by reordering ws-dir → pane → atomic dual-insert) is filed as a
 * separate refactor; this shape is the established equilibrium.
 *
 * Throws after best-effort rollback (deleteAgent) if workspace creation
 * fails. Returns the workspace's on-disk path.
 */
async function prestageWorkspace(db: Db, opts: SpawnAgentOptions, cli: string): Promise<string> {
  insertAgent(db, {
    name: opts.name,
    workstream: opts.workstream,
    cli,
    paneId: pendingPaneIdFor(opts.name),
    role: opts.role,
    tab: opts.tab ?? null,
  });
  try {
    const wsOpts: Parameters<typeof createWorkspace>[1] = {
      agent: opts.name,
      workstream: opts.workstream,
    };
    if (opts.workspaceBackend !== undefined) wsOpts.backend = opts.workspaceBackend;
    if (opts.workspaceFrom !== undefined) wsOpts.parentRef = opts.workspaceFrom;
    if (opts.workspaceProjectRoot !== undefined) wsOpts.projectRoot = opts.workspaceProjectRoot;
    const ws = await createWorkspace(db, wsOpts);
    return ws.path;
  } catch (err) {
    try {
      deleteAgent(db, opts.name, opts.workstream);
    } catch {
      // best-effort: never mask the workspace error
    }
    throw err;
  }
}

/**
 * Stage 2 of spawn (after the real pane exists): either patch the
 * placeholder row to the real pane id (workspace path), or insert a
 * fresh agent row (no-workspace path). Throws if the patch UPDATE
 * silently affects no row (the placeholder row vanished mid-spawn —
 * historically the bug_agent_spawn_workspace_fk_failure class, now
 * patched by reconcile's pending-pane prune skip).
 */
function finalizeAgentRow(
  db: Db,
  args: { opts: SpawnAgentOptions; cli: string; paneId: string; hasWorkspace: boolean },
): AgentRow {
  const { opts, cli, paneId, hasWorkspace } = args;
  if (!hasWorkspace) {
    return insertAgent(db, {
      name: opts.name,
      workstream: opts.workstream,
      cli,
      paneId,
      role: opts.role,
      tab: opts.tab ?? null,
    });
  }
  // Scope the patch to the spawn's workstream so a same-named worker
  // in another workstream isn't repointed at this pane
  // (bug_v5_name_clash_silent_misroute).
  db.prepare(
    `UPDATE agents SET pane_id = ?, updated_at = ?
      WHERE name = ?
        AND workstream_id = (SELECT id FROM workstreams WHERE name = ?)`,
  ).run(paneId, new Date().toISOString(), opts.name, opts.workstream);
  const row = getAgent(db, opts.name, opts.workstream);
  if (!row) throw new Error(`spawnAgent: agent vanished after workspace stage: ${opts.name}`);
  return row;
}

/**
 * Roll back a failed spawn. Idempotent and best-effort: every step
 * swallows its own errors so a partial-cleanup substrate (already-killed
 * pane, no agent row) never masks the original failure.
 *
 * `paneId` is `undefined` when createOrReusePane itself threw before
 * returning a pane id — there's nothing to kill. `hasWorkspace` is
 * `true` when prestageWorkspace succeeded; freeWorkspace is idempotent
 * on a missing workspace so calling it after a same-cycle failure is
 * safe. deleteAgent is also idempotent (no-op on a missing row).
 *
 * `ownsRow` is false when the spawn failed before inserting its own row
 * (e.g. it lost a same-name race); the row then belongs to another
 * agent and is left alone.
 */
type SpawnPhase = "workspace" | "pane" | "finalize" | "liveness";

/** Terminal record for a failed spawn. Best-effort; never throws. */
function emitSpawnFailed(db: Db, opts: SpawnAgentOptions, phase: SpawnPhase, err: unknown): void {
  try {
    const errName = err instanceof Error ? err.constructor.name : typeof err;
    emitEvent(
      db,
      opts.workstream,
      "agent.spawn_failed",
      `agent spawn failed ${opts.name} (phase=${phase}, error=${errName})`,
    );
  } catch {
    // logging is best-effort
  }
}

async function rollbackSpawn(
  db: Db,
  name: string,
  paneId: string | undefined,
  hasWorkspace: boolean,
  ownsRow: boolean,
  workstream: string,
): Promise<void> {
  if (paneId !== undefined) {
    // Best-effort: rollback runs on an already-failing path and must not
    // mask the original error, even if the mux itself went away.
    await activeMux()
      .then((mux) => mux.killPane(paneId))
      .catch(() => {});
  }
  if (hasWorkspace) {
    // Scope cleanup to the spawn's workstream so a same-named worker
    // elsewhere isn't torn down by accident
    // (bug_v5_name_clash_silent_misroute).
    await freeWorkspace(db, name, { workstream }).catch(() => {});
  }
  if (ownsRow) deleteAgent(db, name, workstream);
}

/**
 * After a failed spawn that prestaged a workspace, augment the thrown
 * error with orphan-cleanup hints (`mu workspace orphans` +
 * `mu workspace free`). rollbackSpawn is best-effort — if the
 * freeWorkspace call inside it failed (rmdir blocked, vcs backend
 * refusing to remove a workspace with uncommitted state, etc.), the
 * dir survives and the operator needs to know how to clean it up.
 *
 * Uses the duck-typed `errorNextSteps()` convention (see
 * `hasNextSteps()` in src/output.ts) so this works whether the inner
 * error is a typed mu error already exposing nextSteps
 * (AgentDiedOnSpawnError) or a bare TmuxError / Error from
 * createOrReusePane that has none. Existing hints (if any) are
 * preserved and listed first; the orphan-cleanup hints are appended
 * so the diagnostic-first ordering of typed errors is kept.
 */
function attachOrphanCleanupHint(err: unknown, agent: string, workstream: string): void {
  if (typeof err !== "object" || err === null) return;
  const target = err as { errorNextSteps?: () => NextStep[] };
  const existing =
    typeof target.errorNextSteps === "function" ? target.errorNextSteps.bind(target) : null;
  const orphanHints: NextStep[] = [
    {
      intent: "Check for an orphan workspace dir (rollback is best-effort; may have failed)",
      command: `mu workspace orphans -w ${workstream}`,
    },
    {
      intent: "Free the workspace if it survived the rollback (idempotent on missing)",
      command: `mu workspace free ${agent} -w ${workstream}`,
    },
  ];
  // Best-effort: a frozen or non-extensible error must still reach the
  // caller as itself, just without the extra hints.
  try {
    target.errorNextSteps = () => [...(existing ? existing() : []), ...orphanHints];
  } catch {}
}

/**
 * Default liveness window in milliseconds. 0 disables the check (useful
 * for fast tests that don't want to wait). Override via env var
 * `MU_SPAWN_LIVENESS_MS`.
 */
export function defaultSpawnLivenessMs(): number {
  const raw = process.env.MU_SPAWN_LIVENESS_MS;
  if (raw === undefined) return 1500;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed) || parsed < 0) return 1500;
  return parsed;
}

/**
 * Curated list of patterns that indicate the spawned CLI hit a fatal
 * provider/auth error during startup and is now parked at an error
 * prompt instead of becoming a working agent. Source:
 * `agent_spawn_model_auth_failure_counts_as_live` in the feedback ws.
 *
 * Kept short and case-insensitive on purpose: every entry must match a
 * real, observed dogfood failure mode. Don't add patterns speculatively
 * — the false-positive cost is to roll back a healthy spawn, which the
 * operator then has to re-attempt without the scan (`MU_SPAWN_LIVENESS_MS=0`).
 *
 * Mitigation against scrollback noise from harmless prior-session text:
 * `checkSpawnHealth` only scans the LAST `STARTUP_ERROR_TAIL_LINES`
 * lines of the capture, and after an ok ctl handshake it skips
 * `EXEC_FAILURE_PATTERN` (a resumed pi session re-renders old tool
 * output, which often ends in `No such file or directory`).
 */
const AUTH_ERROR_PATTERNS: readonly RegExp[] = [
  /No API key found for [\w-]+/i,
  /Error: invalid API key/i,
  /Authentication failed/i,
  /401 Unauthorized/i,
  /Could not authenticate/i,
];

/**
 * The spawned command failed to exec (fb_agent_spawn_no_validation
 * part B): post-spawn detection of the
 * "binary not found at exec time" failure mode. The pre-flight check
 * above catches the common typo BEFORE any side effect, but a few
 * edge cases still slip through and only surface in the pane:
 *   - `--command "..."` skips the pre-flight (operator opt-out).
 *   - PATH inside the spawned shell differs from PATH in mu's
 *     process (login shell rc files, /etc/paths.d, etc.).
 *   - Race: binary on PATH at spawn time, gone 1.5s later.
 *
 * Anchored to lines that LOOK like a shell error rather than prose
 * mentioning the marker (review_substrate_startup_err_patterns_too_broad):
 * require the marker at end-of-line, optionally followed by a single
 * `: <name>` token (the zsh form: `zsh: command not found: pi-meta`).
 * This still trips the canonical cases —
 *   `bash: pi-meta: command not found`
 *   `zsh: command not found: pi-meta`
 *   `sh: pi-meta: No such file or directory`
 * — but rejects banner prose like
 *   `I noticed earlier you saw 'command not found'`
 *   `type \`mu\` — command not found? install with …`
 * that previously triggered a full spawn rollback. The pre-flight
 * PATH check remains the deterministic safety net for typo'd `--cli`.
 */
const EXEC_FAILURE_PATTERN =
  /^(?:.*: )?(?:command not found|No such file or directory)(?::\s*\S+)?$/i;

/** True when `line` reads as a shell exec failure, not a provider/auth error. */
export function isExecFailureLine(line: string): boolean {
  return EXEC_FAILURE_PATTERN.test(line);
}

/**
 * Number of trailing lines of the post-liveness scrollback to scan for
 * the startup-error patterns. The capture is `lines: 50`; tailing the last
 * 30 keeps the scan focused on the spawned CLI's own startup output
 * (the pane is brand-new, so anything earlier than that is the shell
 * banner / our own command-line, both safe).
 */
const STARTUP_ERROR_TAIL_LINES = 30;

/**
 * Scan the tail of a freshly-captured pane buffer for known startup-
 * failure patterns. Returns the matched line on first hit, or undefined
 * if the buffer is clean. `cliStarted` (the CLI answered the ctl
 * handshake) drops the exec-failure pattern: the CLI demonstrably ran.
 *
 * Exported for the test suite; not part of the SDK surface.
 */
export function detectSpawnStartupError(
  scrollback: string,
  cliStarted = false,
): string | undefined {
  const patterns = cliStarted
    ? AUTH_ERROR_PATTERNS
    : [...AUTH_ERROR_PATTERNS, EXEC_FAILURE_PATTERN];
  const lines = scrollback.split(/\r?\n/);
  const tail = lines.slice(Math.max(0, lines.length - STARTUP_ERROR_TAIL_LINES));
  for (const line of tail) {
    for (const pattern of patterns) {
      if (pattern.test(line)) return line;
    }
  }
  return undefined;
}

async function awaitSpawnLiveness(
  paneId: string,
  agentName: string,
  command: string,
): Promise<boolean> {
  const ms = defaultSpawnLivenessMs();
  if (ms === 0) return false;
  await sleep(ms);
  return checkSpawnHealth(paneId, agentName, command);
}

/**
 * Claude's "Is this a project you trust?" dialog, recorded from claude
 * 2.1.x (test/fixtures/claude-trust-dialog.txt). It is unnumbered and
 * defaults to "No, exit", so the Enter that ends a send makes claude
 * quit. Matched on the short option line, which survives line wraps.
 *
 * NOT an error: the pane and agent are kept, so this is a warning that
 * spawn reports and that skips the initial --send. Startup scan only: it
 * does not protect a later `mu agent send`, and the herdr spawn path
 * never runs this scan. No keypress is ever sent for the operator.
 */
const CLAUDE_TRUST_DIALOG = /Yes, I trust this folder/i;

/** True when the tail of `scrollback` shows Claude's folder-trust dialog. */
export function detectClaudeTrustDialog(scrollback: string): boolean {
  const lines = scrollback.split(/\r?\n/);
  const tail = lines.slice(Math.max(0, lines.length - STARTUP_ERROR_TAIL_LINES));
  return tail.some((line) => CLAUDE_TRUST_DIALOG.test(line));
}

/**
 * One liveness verdict: throw AgentDiedOnSpawnError when the pane is
 * gone, AgentSpawnStartupError when its scrollback tail shows a known
 * startup failure. Returns true (non-fatal) when Claude's trust dialog is
 * showing.
 */
async function checkSpawnHealth(
  paneId: string,
  agentName: string,
  command: string,
  /** When the failure happened, for the error text; default: the liveness window. */
  when?: string,
  /** The CLI answered the ctl handshake; see detectSpawnStartupError. */
  cliStarted = false,
): Promise<boolean> {
  // Capture-pane first so we have something to attach to the error if the
  // pane is in the process of being torn down (the buffer survives a beat
  // longer than the pane's existence in some tmux builds).
  const mux = await activeMux();
  const scrollback = await mux.capturePane(paneId, { lines: 50 }).catch(() => undefined);
  if (!(await mux.paneExists(paneId))) {
    throw new AgentDiedOnSpawnError(agentName, paneId, scrollback, command, when);
  }
  // Pane is alive — but "alive" doesn't mean "working". Scan the tail of
  // the capture for known provider/auth startup errors
  // (agent_spawn_model_auth_failure_counts_as_live). The pane stays at
  // an error prompt forever otherwise, and the orchestrator only
  // discovers the dud minutes later when `mu task wait` stalls.
  if (scrollback !== undefined) {
    const matchedLine = detectSpawnStartupError(scrollback, cliStarted);
    if (matchedLine !== undefined) {
      throw new AgentSpawnStartupError(agentName, paneId, matchedLine, scrollback, when);
    }
    return detectClaudeTrustDialog(scrollback);
  }
  return false;
}

/**
 * Three cases, all returning a stable pane id:
 *   - session doesn't exist          → create session+window+pane in one shot
 *                                      (with `keepSessionAlive`: create the
 *                                      session with its placeholder `_mu`
 *                                      window first, then a window here)
 *   - session exists, no such window → create a new window for this agent
 *   - session and window both exist  → split the window to add a pane
 *
 * BARE-PANE MODE. A backend implementing `startAgentInPane` has no
 * create-and-run form, so passing a command to the creation verbs is a
 * hard error there (deliberately, so it can never be dropped silently).
 * For those backends this creates the pane EMPTY and the caller starts
 * the agent in it as a second step. Keyed on the capability, never on
 * the backend name; on tmux the command still rides along exactly as
 * before and this function is byte-for-byte equivalent to its previous
 * behaviour.
 */
async function createOrReusePane(opts: {
  session: string;
  /** Ensure the placeholder `_mu` window (creating or repairing it) so
   *  the session outlives its last agent. Set for `scratch`, which is
   *  never `workstream init`-ed; other workstreams get `_mu` at init. */
  keepSessionAlive: boolean;
  windowName: string;
  command: string;
  cwd?: string;
  env?: Record<string, string>;
}): Promise<string> {
  // Load-bearing from top to bottom: this IS the pane creation.
  const mux = await activeMux();
  const command = mux.startAgentInPane === undefined ? opts.command : "";
  if (opts.keepSessionAlive) await ensureWorkstreamSession(opts.session);
  else if (!(await mux.sessionExists(opts.session))) {
    return mux.newSessionWithPane(opts.session, {
      windowName: opts.windowName,
      command,
      cwd: opts.cwd,
      env: opts.env,
    });
  }

  const windows = await mux.listWindows(opts.session);
  const matching = windows.find((w) => w.name === opts.windowName);

  if (matching) {
    return mux.splitWindow({
      // tmux splits a WINDOW: target it as `=<session>:<window_id>`.
      // A bare `<session>:<name>` falls back to tmux's session prefix
      // match, so if `mu-auth` vanishes between listWindows and here,
      // `mu-auth:Backend` splits `mu-auth-refactor`'s Backend window.
      // `=` makes the session exact and the stable window id (from
      // the listing above) must live in that session, so a vanished
      // session or window fails instead of landing elsewhere.
      // herdr splits a PANE and has no window-target form at all. Ask
      // the backend for a concrete pane in that window when it needs
      // one — cheaper than a `target` union type for two backends.
      target:
        mux.startAgentInPane === undefined
          ? `=${opts.session}:${matching.id}`
          : await firstPaneInWindow(mux, opts.session, matching.id),
      command,
      cwd: opts.cwd,
      env: opts.env,
    });
  }

  return mux.newWindow({
    session: opts.session,
    name: opts.windowName,
    command,
    cwd: opts.cwd,
    env: opts.env,
  });
}

/**
 * A concrete pane id inside `windowId`, for backends that split panes
 * rather than windows. Any pane in the window is a correct split target
 * — the new pane lands in the same window either way, which is all mu
 * cares about; geometry is the user's business.
 *
 * Throws rather than guessing when the window is empty: an empty window
 * cannot exist on either backend (a window IS its panes), so seeing one
 * means the window vanished between `listWindows` and here, and the
 * caller must roll back rather than create a pane somewhere else.
 */
async function firstPaneInWindow(
  mux: MuxBackend,
  session: string,
  windowId: string,
): Promise<string> {
  const panes = await mux.listPanesInSession(session);
  const pane = panes.find((p) => p.windowId === windowId);
  if (pane === undefined) {
    throw new Error(
      `spawnAgent: window ${windowId} in session ${session} has no panes to split (it vanished mid-spawn?)`,
    );
  }
  return pane.paneId;
}
